import * as Sentry from '@sentry/react';
import { getSentryUserContext } from './sentryUserContext';

/**
 * `payload`      — HTTP 200 with `insertUserAction.errors` populated. The backend resolver declares
 *                  ForeignKey/Unauthorized/Validation as Hot Chocolate `[Error(...)]` types, so these
 *                  never reach the top-level `errors` array that `relay.ts` watches.
 * `missing_node` — HTTP 200, no errors, and still no `userActionNode`: the row was not written and
 *                  nothing said so.
 * `transport`    — the request itself failed. `relay.ts` also reports these, but only grouped by
 *                  operation name; this one carries the action type that was lost.
 */
export type UserActionFailureKind = 'payload' | 'missing_node' | 'transport';

export interface UserActionPayloadError {
  readonly __typename?: string;
  readonly message?: string;
}

/** The slice of the response both user-action mutations share. */
export interface UserActionMutationResponse {
  readonly insertUserAction?: {
    readonly errors?: ReadonlyArray<UserActionPayloadError> | null;
    readonly userActionNode?: { readonly id: string } | null;
  } | null;
}

/**
 * Application-owned context. Callers construct this explicitly; never spread a mutation response or
 * input object in here — the input carries free-text descriptions and the response carries the
 * written entity.
 */
export interface UserActionFailureFields {
  kind: UserActionFailureKind;
  operationName: string;
  actionTypeId: string;
  description?: string;
  skillId?: string | null;
  errorTypenames?: string;
  errorMessages?: string;
}

const UNKNOWN_ACTION_TYPE = 'unknown';

function truncateString(value: unknown, maxLen = 300): string {
  const s = String(value ?? '');
  return s.length > maxLen ? `${s.slice(0, maxLen - 3)}…` : s;
}

/**
 * Hot Chocolate returns `errors: null` on success, so an empty/absent array is not a failure.
 * Entries without a `message` are the union members we do not select; they still count as errors.
 */
export function getUserActionPayloadErrors(
  response: UserActionMutationResponse | null | undefined,
): ReadonlyArray<UserActionPayloadError> {
  return response?.insertUserAction?.errors ?? [];
}

export function hasUserActionNode(response: UserActionMutationResponse | null | undefined): boolean {
  return Boolean(response?.insertUserAction?.userActionNode?.id);
}

/**
 * One signature per distinct error, preferring the GraphQL type name because it is stable across
 * message wording. Mirrors `getGraphqlErrorSignatures` in `relay.ts`.
 */
function getPayloadErrorSignatures(errors: ReadonlyArray<UserActionPayloadError>): string[] {
  const signatures = errors.map(error =>
    error.__typename ? `type:${error.__typename}` : `message:${truncateString(error.message, 200)}`,
  );

  return [...new Set(signatures)].sort();
}

/**
 * The 401/403 branch in `relay.ts` answers with this message and then signs the user out. Reporting
 * it would fire on every session expiry, for an action nobody expected to survive the logout.
 */
function isUnauthorizedTransportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes('Unauthorized');
}

/** `relay.ts` names the error it throws for a failed fetch; reuse that to inherit the offline filter. */
function isRelayNetworkError(error: unknown): boolean {
  return error instanceof Error && error.name === 'GraphQLNetworkError';
}

function getUserActionTags(fields: UserActionFailureFields): Record<string, string> {
  return {
    source: 'user_action',
    user_action_failure: fields.kind,
    user_action_type: fields.actionTypeId || UNKNOWN_ACTION_TYPE,
    graphql_operation: fields.operationName,
  };
}

function captureUserActionFailure(
  fields: UserActionFailureFields,
  fingerprintTail: string[],
  options: { cause?: unknown; extraTags?: Record<string, string> } = {},
): void {
  const error = new Error(`User action ${fields.kind} failure`);
  error.name = 'UserActionError';

  if (options.cause instanceof Error) {
    Object.defineProperty(error, 'cause', { configurable: true, value: options.cause, writable: true });
  }

  Sentry.withScope(scope => {
    const { user, ...actorTags } = getSentryUserContext();

    scope.setLevel('error');
    scope.setUser(user?.id ? { id: user.id } : null);
    Object.entries({ ...actorTags, ...getUserActionTags(fields), ...options.extraTags }).forEach(([key, value]) =>
      scope.setTag(key, value),
    );

    // High-volume tracking actions (Component Entered/Left fire on nearly every navigation) would
    // otherwise mint one issue per occurrence instead of one issue with a count.
    scope.setFingerprint(['user-action', fields.kind, fields.actionTypeId || UNKNOWN_ACTION_TYPE, ...fingerprintTail]);
    scope.setContext('user_action', { ...fields });

    Sentry.captureException(error);
  });
}

export function reportUserActionPayloadFailure(
  errors: ReadonlyArray<UserActionPayloadError>,
  fields: Omit<UserActionFailureFields, 'kind' | 'errorTypenames' | 'errorMessages'>,
): void {
  const typenames = [...new Set(errors.map(error => error.__typename).filter(Boolean))].join();
  const messages = errors
    .map(error => truncateString(error.message, 200))
    .filter(Boolean)
    .join(' | ');

  captureUserActionFailure(
    {
      ...fields,
      kind: 'payload',
      description: truncateString(fields.description, 200),
      ...(typenames && { errorTypenames: typenames }),
      ...(messages && { errorMessages: messages }),
    },
    getPayloadErrorSignatures(errors),
  );
}

export function reportUserActionMissingNode(fields: Omit<UserActionFailureFields, 'kind'>): void {
  captureUserActionFailure(
    { ...fields, kind: 'missing_node', description: truncateString(fields.description, 200) },
    [],
  );
}

export function reportUserActionTransportFailure(error: unknown, fields: Omit<UserActionFailureFields, 'kind'>): void {
  if (isUnauthorizedTransportError(error)) return;

  captureUserActionFailure(
    {
      ...fields,
      kind: 'transport',
      description: truncateString(fields.description, 200),
      errorMessages: truncateString(error instanceof Error ? error.message : error, 200),
    },
    [],
    {
      cause: error,
      // Tagging this the way `relay.ts` does lets the existing offline filter in `sentryInit.ts`
      // drop it when `navigator.onLine === false`, rather than duplicating that rule here.
      ...(isRelayNetworkError(error) && { extraTags: { graphql_network_error: 'true' } }),
    },
  );
}
